/**
 * Field-level enrichment catalog and provider resolution logic.
 *
 * Defines every data field a user can select when enriching a contact,
 * grouped by category (Person, Company, Financial, News). Each field
 * lists which external providers can supply it.
 *
 * resolveProviders() maps the user's field selections to the minimal
 * set of provider configs the backend expects.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type FieldGroup = 'person' | 'company' | 'financial' | 'news'

export interface FieldDef {
  id: string            // dotted key, e.g. "person.email"
  label: string         // display label
  providers: string[]   // provider IDs that supply this field
  defaultSelected: boolean
}

export interface FieldGroupDef {
  id: FieldGroup
  label: string
  fields: FieldDef[]
}

export interface ProviderConfig {
  name: string
  config: Record<string, any>
}

// ---------------------------------------------------------------------------
// Field Catalog
// ---------------------------------------------------------------------------

export const FIELD_CATALOG: FieldGroupDef[] = [
  {
    id: 'person',
    label: 'Person',
    fields: [
      { id: 'person.name',              label: 'Name (First / Last)',   providers: ['apollo', 'peopledatalabs'],                          defaultSelected: true },
      { id: 'person.full_name',         label: 'Full Name',            providers: ['peopledatalabs', 'explorium'],                       defaultSelected: true },
      { id: 'person.email',             label: 'Email Address',        providers: ['apollo', 'hunter', 'peopledatalabs', 'explorium'],   defaultSelected: true },
      { id: 'person.email_verification',label: 'Email Verification',   providers: ['hunter'],                                           defaultSelected: false },
      { id: 'person.job_title',         label: 'Job Title',            providers: ['apollo', 'peopledatalabs', 'explorium'],             defaultSelected: true },
      { id: 'person.department',        label: 'Department',           providers: ['apollo', 'explorium'],                               defaultSelected: true },
      { id: 'person.seniority',         label: 'Seniority Level',      providers: ['apollo', 'peopledatalabs', 'explorium'],             defaultSelected: true },
      { id: 'person.phone',             label: 'Phone Number',         providers: ['apollo', 'peopledatalabs', 'explorium'],             defaultSelected: false },
      { id: 'person.linkedin',          label: 'LinkedIn URL',         providers: ['apollo', 'peopledatalabs', 'explorium'],             defaultSelected: true },
      { id: 'person.twitter',           label: 'Twitter / X',          providers: ['apollo', 'peopledatalabs'],                          defaultSelected: false },
      { id: 'person.career_history',    label: 'Career History',       providers: ['peopledatalabs', 'explorium'],                       defaultSelected: true },
      { id: 'person.education',         label: 'Education',            providers: ['peopledatalabs', 'explorium'],                       defaultSelected: true },
      { id: 'person.skills',            label: 'Skills',               providers: ['peopledatalabs', 'explorium'],                       defaultSelected: true },
      { id: 'person.interests',         label: 'Interests',            providers: ['peopledatalabs', 'explorium'],                       defaultSelected: false },
    ],
  },
  {
    id: 'company',
    label: 'Company',
    fields: [
      { id: 'company.name_website',     label: 'Name & Website',       providers: ['apollo', 'peopledatalabs', 'explorium'],             defaultSelected: true },
      { id: 'company.description',      label: 'Description',          providers: ['apollo', 'peopledatalabs', 'explorium'],             defaultSelected: true },
      { id: 'company.industry',         label: 'Industry',             providers: ['apollo', 'peopledatalabs', 'explorium'],             defaultSelected: true },
      { id: 'company.employee_count',   label: 'Employee Count',       providers: ['apollo', 'peopledatalabs', 'explorium'],             defaultSelected: true },
      { id: 'company.revenue',          label: 'Revenue',              providers: ['apollo', 'peopledatalabs', 'explorium'],             defaultSelected: true },
      { id: 'company.founded_year',     label: 'Founded Year',         providers: ['apollo', 'peopledatalabs'],                          defaultSelected: false },
      { id: 'company.linkedin',         label: 'Company LinkedIn',     providers: ['apollo', 'peopledatalabs', 'explorium'],             defaultSelected: false },
      { id: 'company.naics_sic',        label: 'NAICS / SIC Code',     providers: ['explorium'],                                        defaultSelected: false },
    ],
  },
  {
    id: 'financial',
    label: 'Financial',
    fields: [
      { id: 'financial.income_statement', label: 'Income Statement',   providers: ['fmp', 'alpha_vantage'],                             defaultSelected: true },
      { id: 'financial.balance_sheet',    label: 'Balance Sheet',      providers: ['fmp', 'alpha_vantage'],                             defaultSelected: true },
      { id: 'financial.cash_flow',        label: 'Cash Flow',          providers: ['fmp', 'alpha_vantage'],                             defaultSelected: true },
      { id: 'financial.stock_metrics',    label: 'Stock & Valuation',  providers: ['fmp', 'alpha_vantage'],                             defaultSelected: true },
      { id: 'financial.sec_facts',        label: 'SEC Financial Facts', providers: ['sec_edgar'],                                       defaultSelected: false },
      { id: 'financial.sec_filings',      label: 'SEC Filings',        providers: ['sec_edgar'],                                        defaultSelected: false },
    ],
  },
  {
    id: 'news',
    label: 'News',
    fields: [
      { id: 'news.company_news',   label: 'Company News',   providers: ['news'],  defaultSelected: true },
      { id: 'news.person_news',    label: 'Person News',    providers: ['news'],  defaultSelected: true },
      { id: 'news.industry_news',  label: 'Industry News',  providers: ['news'],  defaultSelected: false },
    ],
  },
]

// ---------------------------------------------------------------------------
// Action → visible field groups
// ---------------------------------------------------------------------------

/** Maps action IDs / categories to which field groups the user should see. */
export function getVisibleGroups(actionId: string, category: string): FieldGroup[] {
  // AI Research actions
  if (actionId === 'ai_full_research')       return ['person', 'company', 'financial', 'news']
  if (actionId === 'ai_person_research')     return ['person']
  if (actionId === 'ai_company_research')    return ['company']
  if (actionId === 'ai_buying_signals')      return ['person', 'company', 'news']
  if (actionId === 'ai_outreach_hooks')      return ['person', 'company', 'news']
  if (actionId === 'ai_financial_research')  return ['financial']

  // Standard actions by category
  if (category === 'contact_info')  return ['person']
  if (category === 'company_data')  return ['company']
  if (category === 'financials')    return ['financial']
  if (category === 'news')          return ['news']

  // Fallback (verification, etc.) — no field selection
  return []
}

// ---------------------------------------------------------------------------
// Provider resolution
// ---------------------------------------------------------------------------

/** Given a set of selected field IDs, compute the minimal provider configs. */
export function resolveProviders(selectedFieldIds: Set<string>): ProviderConfig[] {
  // Build a lookup: which field groups each provider needs to handle
  const providerFields = new Map<string, Set<string>>()

  for (const group of FIELD_CATALOG) {
    for (const field of group.fields) {
      if (!selectedFieldIds.has(field.id)) continue
      for (const provider of field.providers) {
        if (!providerFields.has(provider)) {
          providerFields.set(provider, new Set())
        }
        providerFields.get(provider)!.add(field.id)
      }
    }
  }

  const configs: ProviderConfig[] = []

  // Apollo
  if (providerFields.has('apollo')) {
    const fields = providerFields.get('apollo')!
    const hasPersonFields = [...fields].some(f => f.startsWith('person.'))
    const hasCompanyFields = [...fields].some(f => f.startsWith('company.'))
    configs.push({
      name: 'apollo',
      config: { enrich_person: hasPersonFields, enrich_company: hasCompanyFields },
    })
  }

  // Hunter
  if (providerFields.has('hunter')) {
    const fields = providerFields.get('hunter')!
    configs.push({
      name: 'hunter',
      config: {
        find_email: fields.has('person.email'),
        verify_email: fields.has('person.email_verification'),
      },
    })
  }

  // People Data Labs
  if (providerFields.has('peopledatalabs')) {
    const fields = providerFields.get('peopledatalabs')!
    const hasPersonFields = [...fields].some(f => f.startsWith('person.'))
    const hasCompanyFields = [...fields].some(f => f.startsWith('company.'))
    configs.push({
      name: 'peopledatalabs',
      config: { enrich_person: hasPersonFields, enrich_company: hasCompanyFields },
    })
  }

  // Explorium
  if (providerFields.has('explorium')) {
    const fields = providerFields.get('explorium')!
    const hasPersonFields = [...fields].some(f => f.startsWith('person.'))
    const hasCompanyFields = [...fields].some(f => f.startsWith('company.'))
    configs.push({
      name: 'explorium',
      config: { enrich_person: hasPersonFields, enrich_company: hasCompanyFields },
    })
  }

  // FMP
  if (providerFields.has('fmp')) {
    const fields = providerFields.get('fmp')!
    const hasStatements = fields.has('financial.income_statement') || fields.has('financial.balance_sheet') || fields.has('financial.cash_flow')
    const hasMetrics = fields.has('financial.stock_metrics')
    configs.push({
      name: 'fmp',
      config: { get_financial_statements: hasStatements, get_stock_metrics: hasMetrics },
    })
  }

  // Alpha Vantage
  if (providerFields.has('alpha_vantage')) {
    const fields = providerFields.get('alpha_vantage')!
    const hasStatements = fields.has('financial.income_statement') || fields.has('financial.balance_sheet') || fields.has('financial.cash_flow')
    const hasMetrics = fields.has('financial.stock_metrics')
    configs.push({
      name: 'alpha_vantage',
      config: { get_financial_statements: hasStatements, get_stock_metrics: hasMetrics },
    })
  }

  // SEC EDGAR
  if (providerFields.has('sec_edgar')) {
    const fields = providerFields.get('sec_edgar')!
    configs.push({
      name: 'sec_edgar',
      config: {
        get_financial_statements: fields.has('financial.sec_facts'),
        get_filings_list: fields.has('financial.sec_filings'),
      },
    })
  }

  // News
  if (providerFields.has('news')) {
    const fields = providerFields.get('news')!
    configs.push({
      name: 'news',
      config: {
        search_company: fields.has('news.company_news'),
        search_person: fields.has('news.person_news'),
        search_industry: fields.has('news.industry_news'),
      },
    })
  }

  return configs
}

/** Get default selected field IDs for the given visible groups. */
export function getDefaultSelectedFields(visibleGroups: FieldGroup[]): Set<string> {
  const selected = new Set<string>()
  for (const group of FIELD_CATALOG) {
    if (!visibleGroups.includes(group.id)) continue
    for (const field of group.fields) {
      if (field.defaultSelected) selected.add(field.id)
    }
  }
  return selected
}
